import React, { FC, Fragment, useCallback, useEffect, useState } from "react";
import { Menu, Transition } from "@headlessui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import ApiService from "services/api.service";
import TokenService from "services/token.service";
import config, { API_BASE_URL_KEY } from "config";

const POLL_MS = 60_000;
const NOTIFICATIONS_KEY = ["inbox-notifications"] as const;

type Channel = "sms" | "email" | "slack";

interface InboxNotification {
  id: string;
  customerId: string;
  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;
  channel: Channel;
  authorType: string;
  content: string | null;
  createdAt: string;
  unreadCount: number;
  workflowId: string | null;
  stepId: string | null;
}

interface InboxNotificationsResponse {
  unread: number;
  notifications: InboxNotification[];
}

interface InboxNotificationsBellProps {
  showLabel?: boolean;
}

interface SseFrame {
  event: string;
  data: any;
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

function parseSseFrames(buffer: string): { frames: SseFrame[]; rest: string } {
  const frames: SseFrame[] = [];
  const parts = buffer.split("\n\n");
  const rest = parts.pop() ?? "";
  for (const part of parts) {
    let event = "message";
    let dataLine = "";
    for (const line of part.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) dataLine += line.slice(5).trim();
    }
    if (!dataLine) continue;
    try {
      frames.push({ event, data: JSON.parse(dataLine) });
    } catch {
      // Ignore malformed SSE frames.
    }
  }
  return { frames, rest };
}

const fetchNotifications = async (): Promise<InboxNotificationsResponse> => {
  const { data } = await ApiService.get<InboxNotificationsResponse>({
    url: "/inbox/notifications?limit=10",
  });
  return {
    unread: Number(data?.unread ?? 0),
    notifications: data?.notifications ?? [],
  };
};

function channelLabel(channel: Channel): string {
  switch (channel) {
    case "sms":
      return "SMS";
    case "email":
      return "Email";
    case "slack":
      return "Slack";
    default:
      return channel;
  }
}

function contactLabel(notification: InboxNotification): string {
  return (
    notification.customerName ||
    notification.customerEmail ||
    notification.customerPhone ||
    `Contact ${notification.customerId.slice(0, 8)}`
  );
}

function shortTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const minutes = Math.max(0, Math.floor(diffMs / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

const BellIcon: FC = () => (
  <svg
    width="18"
    height="20"
    viewBox="0 0 20 22"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M16 7C16 5.4087 15.3679 3.88258 14.2426 2.75736C13.1174 1.63214 11.5913 1 10 1C8.4087 1 6.88258 1.63214 5.75736 2.75736C4.63214 3.88258 4 5.4087 4 7C4 14 1 16 1 16H19C19 16 16 14 16 7Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M11.7295 20C11.5537 20.3031 11.3014 20.5547 10.9978 20.7295C10.6941 20.9044 10.3499 20.9965 9.99953 20.9965C9.64915 20.9965 9.30492 20.9044 9.0013 20.7295C8.69769 20.5547 8.44534 20.3031 8.26953 20"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const InboxNotificationsBell: FC<InboxNotificationsBellProps> = ({
  showLabel = false,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const baseUrl = config.get(API_BASE_URL_KEY);
  const [isAuthed, setIsAuthed] = useState<boolean>(
    () => !!TokenService.getLocalAccessToken()
  );
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [markAllError, setMarkAllError] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIsAuthed(!!TokenService.getLocalAccessToken());
    }, 5_000);
    return () => window.clearInterval(timer);
  }, []);

  const refreshNotifications = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  }, [queryClient]);

  const { data, isPending, isError } = useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: fetchNotifications,
    enabled: isAuthed,
    refetchInterval: POLL_MS,
  });

  useEffect(() => {
    if (!isAuthed) return;
    const onMarkedRead = () => refreshNotifications();
    const onMarkedAllRead = () => refreshNotifications();
    window.addEventListener("inbox:marked-read", onMarkedRead);
    window.addEventListener("inbox:marked-all-read", onMarkedAllRead);
    return () => {
      window.removeEventListener("inbox:marked-read", onMarkedRead);
      window.removeEventListener("inbox:marked-all-read", onMarkedAllRead);
    };
  }, [isAuthed, refreshNotifications]);

  useEffect(() => {
    if (!isAuthed) return;
    let cancelled = false;
    let controller: AbortController | null = null;
    let backoffMs = 1_000;

    const connect = async () => {
      if (cancelled) return;
      const token = TokenService.getLocalAccessToken();
      if (!token) return;

      controller = new AbortController();
      try {
        const resp = await fetch(`${baseUrl}/ai-builder/notifications/stream`, {
          signal: controller.signal,
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
        });
        if (!resp.ok || !resp.body) {
          throw new Error(`stream HTTP ${resp.status}`);
        }
        backoffMs = 1_000;

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!cancelled) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const { frames, rest } = parseSseFrames(buffer);
          buffer = rest;
          for (const frame of frames) {
            if (
              frame.event === "inbox_update" ||
              frame.event === "inbox_marked_read" ||
              frame.event === "inbox_marked_all_read"
            ) {
              refreshNotifications();
            }
          }
        }
      } catch (e: any) {
        if (cancelled || e?.name === "AbortError") return;
      }

      if (cancelled) return;
      await new Promise((resolve) => window.setTimeout(resolve, backoffMs));
      backoffMs = Math.min(backoffMs * 2, 30_000);
      void connect();
    };

    void connect();
    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [isAuthed, baseUrl, refreshNotifications]);

  const unread = data?.unread ?? 0;
  const notifications = data?.notifications ?? [];

  const openNotification = (notification: InboxNotification) => {
    navigate("/messages", {
      state: {
        customerId: notification.customerId,
        messageId: notification.id,
      },
    });
  };

  const markAllRead = async () => {
    if (unread <= 0 || markingAllRead) return;
    setMarkingAllRead(true);
    setMarkAllError(null);
    try {
      await ApiService.post({ url: "/inbox/read-all", options: {} as any });
      queryClient.setQueryData<InboxNotificationsResponse>(NOTIFICATIONS_KEY, {
        unread: 0,
        notifications: [],
      });
      window.dispatchEvent(new CustomEvent("inbox:marked-all-read"));
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
      void queryClient.invalidateQueries({ queryKey: ["inbox-conversations"] });
    } catch {
      setMarkAllError("Could not mark messages read.");
    } finally {
      setMarkingAllRead(false);
    }
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button
        className={`relative flex h-8 items-center justify-center text-themed-text transition-colors focus:outline-none focus:ring-2 focus:ring-themed-ring ${
          showLabel
            ? "w-8 gap-0 rounded-lg border border-themed-border bg-transparent px-0 text-[12px] font-semibold text-themed-textSecondary hover:bg-themed-elevated hover:text-themed-text xl:w-auto xl:gap-2 xl:px-3"
            : "w-8 rounded-full hover:bg-themed-background"
        }`}
        aria-label={
          unread > 0
            ? `Open notifications, ${unread} unread messages`
            : "Open notifications"
        }
      >
        {showLabel ? (
          <>
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                unread > 0
                  ? "bg-[var(--danger)]"
                  : "bg-themed-textSecondary opacity-50"
              }`}
            />
            <span className="hidden xl:inline">Notifications</span>
          </>
        ) : (
          <BellIcon />
        )}
        {unread > 0 && !showLabel && (
          <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-[#EF4444] px-1 text-[10px] font-bold leading-none text-white ring-2 ring-themed-card">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-[120] mt-2 w-[360px] origin-top-right overflow-hidden rounded-md border border-themed-border bg-themed-card shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="flex items-center justify-between border-b border-themed-border px-4 py-3">
            <div className="text-[14px] font-semibold text-themed-text">
              Notifications
            </div>
            <div className="text-[12px] text-themed-textSecondary">
              {unread > 0 ? `${unread} unread` : "All caught up"}
            </div>
          </div>

          <div className="max-h-[360px] overflow-y-auto">
            {isPending ? (
              <div className="flex flex-col gap-2 p-4">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className="h-14 rounded-md bg-themed-border/30 animate-pulse"
                  />
                ))}
              </div>
            ) : isError ? (
              <div className="px-4 py-5 text-[13px] text-themed-textSecondary">
                Notifications could not be loaded.
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-[13px] text-themed-textSecondary">
                No unread messages.
              </div>
            ) : (
              notifications.map((notification) => (
                <Menu.Item key={notification.id}>
                  {({ active }) => (
                    <button
                      type="button"
                      onClick={() => openNotification(notification)}
                      className={classNames(
                        active ? "bg-themed-muted" : "",
                        "flex w-full gap-3 border-b border-themed-border/60 px-4 py-3 text-left last:border-b-0"
                      )}
                    >
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#EF4444]" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center justify-between gap-2">
                          <span className="truncate text-[13px] font-semibold text-themed-text">
                            {contactLabel(notification)}
                          </span>
                          <span className="shrink-0 text-[11px] text-themed-textSecondary">
                            {shortTime(notification.createdAt)}
                          </span>
                        </span>
                        <span className="mt-0.5 flex items-center gap-1.5">
                          <span className="rounded bg-red-50 px-1.5 text-[10px] font-medium leading-[1.6] text-red-700">
                            {channelLabel(notification.channel)}
                          </span>
                          {notification.unreadCount > 1 && (
                            <span className="text-[11px] text-themed-textSecondary">
                              {notification.unreadCount} unread
                            </span>
                          )}
                        </span>
                        <span className="mt-1 block truncate text-[12px] text-themed-textSecondary">
                          {notification.content || "(no content)"}
                        </span>
                      </span>
                    </button>
                  )}
                </Menu.Item>
              ))
            )}
          </div>

          {markAllError && (
            <div className="border-t border-themed-border px-4 py-2 text-[12px] text-red-600">
              {markAllError}
            </div>
          )}

          <div className="grid grid-cols-2 border-t border-themed-border">
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  disabled={unread <= 0 || markingAllRead}
                  onClick={() => void markAllRead()}
                  className={classNames(
                    active && unread > 0 ? "bg-themed-muted" : "",
                    "block w-full px-4 py-3 text-center text-[13px] font-medium text-themed-textSecondary disabled:cursor-not-allowed disabled:opacity-40"
                  )}
                >
                  {markingAllRead ? "Marking..." : "Mark all read"}
                </button>
              )}
            </Menu.Item>
            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => navigate("/messages")}
                  className={classNames(
                    active ? "bg-themed-muted" : "",
                    "block w-full border-l border-themed-border px-4 py-3 text-center text-[13px] font-medium text-[#4F46E5]"
                  )}
                >
                  View messages
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default InboxNotificationsBell;
